import api from '../api/apiClient';

/**
 * Detects device type, browser, and operating system from user agent
 */
const getDeviceInfo = () => {
    const ua = navigator.userAgent;

    // Detect device type
    let device = 'desktop';
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        device = 'tablet';
    } else if (
        /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)
    ) {
        device = 'mobile';
    }

    // Detect browser
    let browser = 'Unknown';
    if (ua.indexOf('Firefox') > -1) {
        browser = 'Firefox';
    } else if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) {
        browser = 'Opera';
    } else if (ua.indexOf('Trident') > -1) {
        browser = 'Internet Explorer';
    } else if (ua.indexOf('Edge') > -1) {
        browser = 'Edge';
    } else if (ua.indexOf('Chrome') > -1) {
        browser = 'Chrome';
    } else if (ua.indexOf('Safari') > -1) {
        browser = 'Safari';
    }

    // Detect OS
    let os = 'Unknown';
    if (ua.indexOf('Win') > -1) {
        os = 'Windows';
    } else if (ua.indexOf('Android') > -1) {
        os = 'Android';
    } else if (ua.indexOf('like Mac') > -1 || /iPad|iPhone|iPod/.test(ua)) {
        os = 'iOS';
    } else if (ua.indexOf('Mac') > -1) {
        os = 'macOS';
    } else if (ua.indexOf('X11') > -1 || ua.indexOf('Linux') > -1) {
        os = 'Linux';
    }

    return { device, browser, os };
};

/**
 * Track a page view (for logged-out users only)
 * Uses sessionStorage to prevent duplicate tracking in same session
 * @param {string} page - The page path being viewed
 * @param {string} referrer - The referrer URL
 * @param {boolean} converted - Whether this is a conversion (successful sign-in)
 */
export const trackPageView = async (page, referrer = '', converted = false) => {
    try {
        // Check if page view was already tracked this session
        const sessionKey = `captions_tracked_pageview_${page}`;
        if (sessionStorage.getItem(sessionKey)) {
            return; // Already tracked this session
        }

        // Mark as tracked BEFORE the API call to prevent race conditions
        sessionStorage.setItem(sessionKey, 'true');

        const { device, browser, os } = getDeviceInfo();

        await api.post('/api/captions/track', {
            page,
            referrer,
            converted,
            device,
            browser,
            os,
        });
    } catch (error) {
        // Silently fail - don't disrupt user experience for analytics
    }
};

/**
 * Track an event (scroll depth, button clicks, etc.)
 * Uses sessionStorage to prevent duplicate tracking in same session
 * @param {string} eventName - The event name (e.g., 'scroll:hero', 'click:cta')
 */
export const trackEvent = async (eventName) => {
    try {
        // Check if event was already tracked this session
        const sessionKey = `captions_tracked_${eventName}`;
        if (sessionStorage.getItem(sessionKey)) {
            return; // Already tracked this session
        }

        // Mark as tracked BEFORE the API call to prevent race conditions
        sessionStorage.setItem(sessionKey, 'true');

        const { device, browser, os } = getDeviceInfo();

        await api.post('/api/captions/track', {
            page: eventName,
            referrer: document.referrer || '',
            converted: false,
            device,
            browser,
            os,
        });
    } catch (error) {
        // Silently fail - don't disrupt user experience for analytics
    }
};

/**
 * Track a conversion (successful Google sign-in)
 */
export const trackConversion = async () => {
    try {
        const { device, browser, os } = getDeviceInfo();

        await api.post('/api/captions/track', {
            page: 'conversion:google_signin',
            referrer: document.referrer || '',
            converted: true,
            device,
            browser,
            os,
        });
    } catch (error) {
        // Silently fail - don't disrupt user experience for analytics
    }
};

/** Track every successful OAuth sign-in, including returning users. */
export const trackSignIn = async () => {
    try {
        const { device, browser, os } = getDeviceInfo();
        await api.post('/api/captions/track', {
            page: 'auth:successful_signin',
            referrer: document.referrer || '',
            converted: false,
            device,
            browser,
            os,
        });
    } catch {
        // Silently fail - don't disrupt authentication for analytics.
    }
};

export const trackCameraStarted = async (sessionId) => {
    if (!sessionId) return;
    try {
        await api.post('/api/captions/analytics/camera-start', { sessionId });
    } catch {
        // Product analytics must never interrupt the camera.
    }
};

export const trackCameraTranslation = async (sessionId) => {
    if (!sessionId) return;
    try {
        await api.post('/api/captions/analytics/translation-success', {
            sessionId,
        });
    } catch {
        // Product analytics must never interrupt translation.
    }
};
